import { useId, useState, type ReactNode } from "react";
import styles from "./HoverNote.module.css";

/**
 * A short phrase that shows a note beneath it on hover, on focus (it can be tabbed to, and tapped on a touch screen), and hides it
 * again on Escape even while the pointer rests on it (WCAG 1.4.13). The note describes the phrase for a screen reader, so it is
 * read out without being opened. Used where a figure has a detail that would otherwise push the rows below it down.
 *
 * @param props.note - The note.
 * @param props.className - The phrase's class.
 * @param props.children - The phrase.
 */
export function HoverNote({ note, className, children }: { note: string; className?: string; children: ReactNode }): React.JSX.Element {
  const noteId = useId();
  const [dismissed, setDismissed] = useState(false);

  return (
    <span
      className={dismissed ? `${styles.anchor} ${styles.dismissed}` : styles.anchor}
      onMouseLeave={() => setDismissed(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setDismissed(true);
        }
      }}
    >
      <span className={`${styles.trigger} ${className ?? ""}`} tabIndex={0} aria-describedby={noteId} onBlur={() => setDismissed(false)}>
        {children}
      </span>
      <span id={noteId} role="tooltip" className={styles.note}>
        {note}
      </span>
    </span>
  );
}
